import { CSSProperties, ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
// z-index note: the literals below (9998 backdrop / 9999 panel) are mirrored in `constants/layers`, which the
// snackbar reads so it can stay ABOVE this. Change them here and `constants/__tests__/layers.test.ts` fails —
// update both, or an error raised inside a modal goes back to being hidden behind it.
import { X } from 'lucide-react'
import { isTopmostOverlay, lockBodyScroll } from './overlayStack'

export interface ModalProps {
    open: boolean
    onClose: () => void
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'cover' | 'full'
    header?: ReactNode
    footer?: ReactNode
    showCloseButton?: boolean
    children: ReactNode
    style?: CSSProperties
    /** Center the dialog with flexbox instead of `transform: translate(-50%,-50%)`. A CSS transform on any
     *  ancestor breaks Google Maps tile rendering (tiles paint solid grey), so map-hosting modals opt into
     *  this transform-free positioning. Ignored for `size="full"` (already transform-free). */
    disableTransform?: boolean
    /** Test hook rendered on the dialog element (header + content + footer all inside its scope).
     *  Divergence from @numeo-ai/components: without it, testids passed to Modal silently
     *  vanish (no prop spread), stranding e2e selectors. */
    'data-testid'?: string
    /** Accessible dialog name when the header is not wired through aria-labelledby. */
    ariaLabel?: string
}

const sizeWidths: Record<string, string> = {
    xs: '320px',
    sm: '480px',
    md: '640px',
    lg: '800px',
    cover: 'calc(100vw - 80px)',
    full: '100vw',
}

const keyframesStyle = `
@keyframes numeo-modal-backdrop-in { from { opacity: 0 } to { opacity: 1 } }
@keyframes numeo-modal-in { from { opacity: 0; transform: translate(-50%, -48%) scale(0.97) } to { opacity: 1; transform: translate(-50%, -50%) scale(1) } }
@keyframes numeo-modal-full-in { from { opacity: 0 } to { opacity: 1 } }
@keyframes numeo-modal-fade-in { from { opacity: 0 } to { opacity: 1 } }
`

export const Modal = ({
    open,
    onClose,
    size = 'sm',
    header,
    footer,
    showCloseButton = true,
    children,
    style,
    disableTransform = false,
    'data-testid': dataTestId,
    ariaLabel,
}: ModalProps) => {
    const dialogRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isTopmostOverlay(dialogRef.current)) onClose()
        }
        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [open, onClose])

    // Scroll lock
    useEffect(() => {
        if (!open) return
        return lockBodyScroll()
    }, [open])

    if (!open) return null

    const isFull = size === 'full'
    const isCover = size === 'cover'
    // Flexbox centering (no transform) for map-hosting modals; `full` is already transform-free.
    const isFlex = disableTransform && !isFull

    const positionClasses = isFull
        ? 'fixed left-0 top-0 z-[9999] h-screen max-h-screen max-w-[100vw] animate-[numeo-modal-full-in_0.2s_ease] rounded-none'
        : isFlex
          ? 'pointer-events-auto relative max-h-[calc(100vh-64px)] max-w-[calc(100vw-32px)] animate-[numeo-modal-fade-in_0.2s_ease] rounded-3xl shadow-overlay'
          : `fixed left-1/2 top-1/2 z-[9999] max-w-[calc(100vw-32px)] animate-[numeo-modal-in_0.2s_ease] rounded-3xl shadow-overlay ${
                isCover ? 'max-h-[calc(100vh-80px)]' : 'max-h-[calc(100vh-64px)]'
            }`

    const dialog = (
        <div
            ref={dialogRef}
            data-ds2-overlay
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            data-testid={dataTestId}
            className={`flex flex-col overflow-hidden bg-surface ${positionClasses}`}
            style={{ width: sizeWidths[size], transform: isFull || isFlex ? undefined : 'translate(-50%, -50%)', ...style }}
        >
            {/* Close button */}
            {showCloseButton && (
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute right-4 top-4 z-[1] flex cursor-pointer items-center rounded-xl bg-default p-1 text-default-foreground hover:bg-default-hover"
                >
                    <X size={16} strokeWidth={1.5} />
                </button>
            )}

            {/* Header — pinned outside the scroll area so it stays visible on long forms */}
            {header && <div className={`shrink-0 px-6 pt-6 ${showCloseButton ? 'pr-12' : ''}`}>{header}</div>}

            {/* Content */}
            <div className={`flex flex-1 flex-col gap-2 overflow-y-auto px-6 pb-6 ${header ? 'pt-2' : 'pt-6'}`}>{children}</div>

            {/* Footer */}
            {footer && <div className="flex shrink-0 justify-end gap-2 px-6 pb-6">{footer}</div>}
        </div>
    )

    const modal = (
        <>
            <style>{keyframesStyle}</style>

            {/* Backdrop */}
            <div onClick={onClose} className="fixed inset-0 z-[9998] animate-[numeo-modal-backdrop-in_0.2s_ease] bg-black/50 backdrop-blur-[12px]" />

            {/* Flex-centered wrapper stays transform-free (pointer-events pass through to the backdrop so an
                outside click still closes); otherwise the dialog positions itself via transform. */}
            {isFlex ? <div className="pointer-events-none fixed inset-0 z-[9999] flex items-center justify-center p-4">{dialog}</div> : dialog}
        </>
    )

    // Portal to body so the fixed-position overlay escapes any transformed / overflowing
    // ancestor (a dnd-kit drag context, a scroll container). Without this a `transform` on a
    // parent re-anchors `position: fixed` to that parent and the panel gets squeezed inside it.
    return createPortal(modal, document.body)
}
